import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  panelContainer: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
  },
  panel: {
    flex: 1,
    minWidth: '300px',
    overflowX: 'auto',
  },
  panelLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  summaryRow: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid #f3f4f6',
  },
  summaryItem: {
    fontSize: '12px',
    color: '#6b7280',
  },
  summaryValue: {
    fontWeight: 600,
    color: '#1f2937',
    marginLeft: '4px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  legend: {
    display: 'flex',
    gap: '16px',
    marginTop: '8px',
    marginBottom: '4px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
};

const BAR_MARGIN = { top: 20, right: 20, bottom: 30, left: 140 };
const SCATTER_MARGIN = { top: 20, right: 50, bottom: 40, left: 50 };

function getCorrelationLabel(r) {
  const absR = Math.abs(r);
  if (absR >= 0.8) return 'Strong';
  if (absR >= 0.5) return 'Moderate';
  if (absR >= 0.3) return 'Weak';
  return 'Very weak';
}

function getCorrelationColor(r) {
  if (r >= 0.7) return '#16a34a';
  if (r >= 0.4) return '#65a30d';
  if (r >= 0.1) return '#a3a3a3';
  if (r >= -0.1) return '#9ca3af';
  if (r >= -0.4) return '#f59e0b';
  if (r >= -0.7) return '#ea580c';
  return '#dc2626';
}

function formatPairLabel(fieldA, fieldB) {
  return `${fieldA} vs ${fieldB}`;
}

export default function CrossCorrelationChart({ data }) {
  const barSvgRef = useRef(null);
  const scatterSvgRef = useRef(null);
  const barContainerRef = useRef(null);
  const scatterContainerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const pairs = useMemo(() => {
    if (!data || !data.pairs || data.pairs.length === 0) return [];
    return data.pairs;
  }, [data]);

  // Default selection: strongest correlated pair
  const effectiveIndex = useMemo(() => {
    if (pairs.length === 0) return -1;
    if (selectedIndex !== null && selectedIndex >= 0 && selectedIndex < pairs.length) {
      return selectedIndex;
    }
    // Find strongest correlation by absolute value
    let bestIdx = 0;
    let bestAbs = 0;
    pairs.forEach((p, i) => {
      const absC = Math.abs(p.correlation);
      if (absC > bestAbs) {
        bestAbs = absC;
        bestIdx = i;
      }
    });
    return bestIdx;
  }, [pairs, selectedIndex]);

  const selectedPair = useMemo(() => {
    if (effectiveIndex < 0 || effectiveIndex >= pairs.length) return null;
    return pairs[effectiveIndex];
  }, [pairs, effectiveIndex]);

  // Draw correlation bar chart (left panel)
  useEffect(() => {
    if (!pairs || pairs.length === 0) return;

    const svg = d3.select(barSvgRef.current);
    svg.selectAll('*').remove();

    const viewW = 700;
    const viewH = 400;
    svg.attr('viewBox', `0 0 ${viewW} ${viewH}`);

    const innerW = viewW - BAR_MARGIN.left - BAR_MARGIN.right;
    const innerH = viewH - BAR_MARGIN.top - BAR_MARGIN.bottom;

    const g = svg.append('g')
      .attr('transform', `translate(${BAR_MARGIN.left}, ${BAR_MARGIN.top})`);

    // Sorted pairs by absolute correlation descending
    const sorted = [...pairs].sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));

    // Scales
    const x = d3.scaleLinear()
      .domain([-1, 1])
      .range([0, innerW]);

    const y = d3.scaleBand()
      .domain(sorted.map((_, i) => i))
      .range([0, innerH])
      .padding(0.25);

    // Grid lines
    const gridValues = [-1, -0.5, 0, 0.5, 1];
    g.selectAll('.grid-v')
      .data(gridValues)
      .enter()
      .append('line')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Zero reference line
    g.append('line')
      .attr('x1', x(0))
      .attr('x2', x(0))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4,3');

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .tickValues([-1, -0.5, 0, 0.5, 1])
          .tickSize(0)
          .tickFormat((d) => d.toFixed(1))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Bars
    sorted.forEach((pair, i) => {
      const realIndex = pairs.indexOf(pair);
      const barY = y(i);
      const barH = y.bandwidth();
      const corr = pair.correlation;
      const barX = corr >= 0 ? x(0) : x(corr);
      const barW = Math.abs(x(corr) - x(0));
      const isSelected = realIndex === effectiveIndex;

      // Row label
      g.append('text')
        .attr('x', -8)
        .attr('y', barY + barH / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'end')
        .attr('font-size', '11px')
        .attr('fill', isSelected ? '#1f2937' : '#6b7280')
        .attr('font-weight', isSelected ? '600' : '400')
        .text(formatPairLabel(pair.fieldA, pair.fieldB));

      // Bar rect
      const rect = g.append('rect')
        .attr('x', barX)
        .attr('y', barY)
        .attr('width', Math.max(barW, 2))
        .attr('height', barH)
        .attr('fill', getCorrelationColor(corr))
        .attr('rx', 3)
        .attr('ry', 3)
        .attr('opacity', isSelected ? 1 : 0.7)
        .attr('stroke', isSelected ? '#1f2937' : 'none')
        .attr('stroke-width', isSelected ? 1.5 : 0)
        .style('cursor', 'pointer');

      // Correlation value label
      const labelX = corr >= 0 ? x(corr) + 6 : x(corr) - 6;
      g.append('text')
        .attr('x', labelX)
        .attr('y', barY + barH / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', corr >= 0 ? 'start' : 'end')
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', '#374151')
        .text(corr.toFixed(3))
        .style('pointer-events', 'none');

      // Invisible hit area for the full row
      g.append('rect')
        .attr('x', 0)
        .attr('y', barY)
        .attr('width', innerW)
        .attr('height', barH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('click', () => {
          setSelectedIndex(realIndex);
        })
        .on('mousemove', (event) => {
          rect.attr('opacity', 1);
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            type: 'bar',
            pair: formatPairLabel(pair.fieldA, pair.fieldB),
            correlation: corr,
            interpretation: getCorrelationLabel(corr),
            lag: pair.lag,
          });
        })
        .on('mouseleave', () => {
          rect.attr('opacity', isSelected ? 1 : 0.7);
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [pairs, effectiveIndex]);

  // Draw scatter / dual-axis chart (right panel)
  useEffect(() => {
    if (!selectedPair || !selectedPair.timeSeries || selectedPair.timeSeries.length === 0) {
      const svg = d3.select(scatterSvgRef.current);
      svg.selectAll('*').remove();
      return;
    }

    const svg = d3.select(scatterSvgRef.current);
    svg.selectAll('*').remove();

    const viewW = 700;
    const viewH = 400;
    svg.attr('viewBox', `0 0 ${viewW} ${viewH}`);

    const innerW = viewW - SCATTER_MARGIN.left - SCATTER_MARGIN.right;
    const innerH = viewH - SCATTER_MARGIN.top - SCATTER_MARGIN.bottom;

    const g = svg.append('g')
      .attr('transform', `translate(${SCATTER_MARGIN.left}, ${SCATTER_MARGIN.top})`);

    const ts = selectedPair.timeSeries.map((d) => ({
      ...d,
      date: new Date(d.date),
    })).sort((a, b) => a.date - b.date);

    // Scales
    const xExtent = d3.extent(ts, (d) => d.date);
    const x = d3.scaleTime()
      .domain(xExtent)
      .range([0, innerW]);

    const yAExtent = d3.extent(ts, (d) => d.valueA);
    const yAPadding = (yAExtent[1] - yAExtent[0]) * 0.1 || 5;
    const yA = d3.scaleLinear()
      .domain([yAExtent[0] - yAPadding, yAExtent[1] + yAPadding])
      .nice()
      .range([innerH, 0]);

    const yBExtent = d3.extent(ts, (d) => d.valueB);
    const yBPadding = (yBExtent[1] - yBExtent[0]) * 0.1 || 5;
    const yB = d3.scaleLinear()
      .domain([yBExtent[0] - yBPadding, yBExtent[1] + yBPadding])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid
    g.selectAll('.grid-h')
      .data(yA.ticks(5))
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yA(d))
      .attr('y2', (d) => yA(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(6)
          .tickSize(0)
          .tickFormat(d3.timeFormat('%b %d'))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('dy', '0.8em');

    // Left Y axis (fieldA - blue)
    g.append('g')
      .call(
        d3.axisLeft(yA)
          .ticks(5)
          .tickSize(0)
          .tickFormat((d) => `$${d}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#2563eb');

    // Left Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -38)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#2563eb')
      .attr('font-weight', 500)
      .text(selectedPair.fieldA);

    // Right Y axis (fieldB - orange)
    g.append('g')
      .attr('transform', `translate(${innerW}, 0)`)
      .call(
        d3.axisRight(yB)
          .ticks(5)
          .tickSize(0)
          .tickFormat((d) => `$${d}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#ea580c');

    // Right Y axis label
    g.append('text')
      .attr('transform', 'rotate(90)')
      .attr('x', innerH / 2)
      .attr('y', -innerW - 38)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#ea580c')
      .attr('font-weight', 500)
      .text(selectedPair.fieldB);

    // Line for fieldA (blue)
    const lineA = d3.line()
      .x((d) => x(d.date))
      .y((d) => yA(d.valueA))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(ts)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 1.5)
      .attr('stroke-opacity', 0.6)
      .attr('d', lineA);

    // Line for fieldB (orange)
    const lineB = d3.line()
      .x((d) => x(d.date))
      .y((d) => yB(d.valueB))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(ts)
      .attr('fill', 'none')
      .attr('stroke', '#ea580c')
      .attr('stroke-width', 1.5)
      .attr('stroke-opacity', 0.6)
      .attr('d', lineB);

    // Dots for fieldA (blue)
    g.selectAll('.dot-a')
      .data(ts)
      .enter()
      .append('circle')
      .attr('class', 'dot-a')
      .attr('cx', (d) => x(d.date))
      .attr('cy', (d) => yA(d.valueA))
      .attr('r', 3.5)
      .attr('fill', '#2563eb')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1);

    // Dots for fieldB (orange)
    g.selectAll('.dot-b')
      .data(ts)
      .enter()
      .append('circle')
      .attr('class', 'dot-b')
      .attr('cx', (d) => x(d.date))
      .attr('cy', (d) => yB(d.valueB))
      .attr('r', 3.5)
      .attr('fill', '#ea580c')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1);

    // Invisible overlay for hover detection
    g.selectAll('.hover-zone')
      .data(ts)
      .enter()
      .append('rect')
      .attr('x', (d, i) => {
        const prev = i > 0 ? x(ts[i - 1].date) : x(d.date);
        return (prev + x(d.date)) / 2;
      })
      .attr('y', 0)
      .attr('width', (d, i) => {
        const left = i > 0 ? (x(ts[i - 1].date) + x(d.date)) / 2 : x(d.date);
        const right = i < ts.length - 1 ? (x(d.date) + x(ts[i + 1].date)) / 2 : x(d.date);
        return Math.max(right - left, 8);
      })
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event, d) => {
        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          type: 'scatter',
          date: d.date,
          fieldA: selectedPair.fieldA,
          fieldB: selectedPair.fieldB,
          valueA: d.valueA,
          valueB: d.valueB,
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [selectedPair]);

  if (!data || !data.pairs || data.pairs.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Field Cross-Correlation</div>
        <div style={styles.subtitle}>Correlation between different price service tiers</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  const summary = data.summary || {};

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.title}>Price Field Cross-Correlation</div>
          <div style={styles.subtitle}>Correlation between different price service tiers</div>
        </div>
      </div>

      <div style={styles.panelContainer}>
        {/* Left panel: correlation bar chart */}
        <div style={styles.panel}>
          <div style={styles.panelLabel}>Correlation Coefficients</div>
          <div ref={barContainerRef} style={{ overflowX: 'auto' }}>
            <svg
              ref={barSvgRef}
              style={{ width: '100%', height: 'auto' }}
              preserveAspectRatio="xMidYMid meet"
            />
          </div>
        </div>

        {/* Right panel: dual-axis scatter/line */}
        <div style={styles.panel}>
          <div style={styles.panelLabel}>
            {selectedPair
              ? `Temporal Relationship: ${selectedPair.fieldA} vs ${selectedPair.fieldB}`
              : 'Select a pair'}
          </div>
          {selectedPair && selectedPair.timeSeries && selectedPair.timeSeries.length > 0 ? (
            <>
              <div style={styles.legend}>
                <div style={styles.legendItem}>
                  <span style={{ ...styles.legendDot, background: '#2563eb' }} />
                  <span>{selectedPair.fieldA}</span>
                </div>
                <div style={styles.legendItem}>
                  <span style={{ ...styles.legendDot, background: '#ea580c' }} />
                  <span>{selectedPair.fieldB}</span>
                </div>
              </div>
              <div ref={scatterContainerRef} style={{ overflowX: 'auto' }}>
                <svg
                  ref={scatterSvgRef}
                  style={{ width: '100%', height: 'auto' }}
                  preserveAspectRatio="xMidYMid meet"
                />
              </div>
            </>
          ) : (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: '40px 0', fontSize: '13px' }}>
              {selectedPair ? 'No time series data for this pair' : 'Click a bar to view temporal detail'}
            </div>
          )}
        </div>
      </div>

      {/* Summary stats */}
      <div style={styles.summaryRow}>
        {summary.strongestPair && (
          <div style={styles.summaryItem}>
            Strongest pair:
            <span style={styles.summaryValue}>{summary.strongestPair}</span>
          </div>
        )}
        {summary.weakestPair && (
          <div style={styles.summaryItem}>
            Weakest pair:
            <span style={styles.summaryValue}>{summary.weakestPair}</span>
          </div>
        )}
        {summary.avgCorrelation != null && (
          <div style={styles.summaryItem}>
            Avg correlation:
            <span style={styles.summaryValue}>{summary.avgCorrelation.toFixed(3)}</span>
          </div>
        )}
      </div>

      {/* Tooltip */}
      {tooltip && tooltip.type === 'bar' && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.pair}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Correlation</span>
            <span style={{ fontWeight: 600 }}>{tooltip.correlation.toFixed(3)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Strength</span>
            <span style={{ fontWeight: 600 }}>{tooltip.interpretation}</span>
          </div>
          {tooltip.lag != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Lag</span>
              <span style={{ fontWeight: 600 }}>{tooltip.lag} day{Math.abs(tooltip.lag) !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}
      {tooltip && tooltip.type === 'scatter' && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {d3.timeFormat('%b %d, %Y')(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#2563eb' }}>{tooltip.fieldA}</span>
            <span style={{ fontWeight: 600 }}>
              {tooltip.valueA != null ? `$${tooltip.valueA.toFixed(2)}` : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#ea580c' }}>{tooltip.fieldB}</span>
            <span style={{ fontWeight: 600 }}>
              {tooltip.valueB != null ? `$${tooltip.valueB.toFixed(2)}` : '--'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
